import { Box, Flex } from "@radix-ui/themes";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import FilterSection from "@/components/Marketplace/FilterSection";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

export function CreatorPageSkeleton() {
  const isDesktopLayout = useMediaQuery("(min-width: 1024px)");

  return (
    <div
      className="w-full min-h-full"
      style={{
        background:
          "radial-gradient(ellipse at center top, rgba(107, 143, 163, 0.12) 0%, rgba(107, 143, 163, 0.06) 50%, #fafbfd 70%)",
      }}
    >
      <ContainerWrapper>
        <Flex
          direction={isDesktopLayout ? "row" : "column"}
          className={cn(
            "w-full self-center justify-self-center",
            isDesktopLayout ? "items-start pt-[25px] pb-[80px]" : "",
          )}
        >
          {isDesktopLayout && <FilterSection />}
          <Flex
            direction="column"
            className={cn(
              "flex-1 min-w-0 gap-6 px-4 pt-4 relative",
              isDesktopLayout && "pl-0 lg:pl-[50px]",
            )}
          >
            {/* Content wrapper */}
            <div className="relative w-full">
              {/* Main Content: Avatar + (Header + About + Links), then full-width table */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-10 w-full mt-4">
                {/* Left Column: Avatar first on mobile */}
                <div className="w-full flex flex-col order-1">
                  <Flex direction="column" className="gap-6">
                    <Flex direction="column" className="gap-4">
                      <div className="relative w-full">
                        <div className="w-full max-w-full aspect-square rounded-full overflow-hidden shrink-0 border-[8px] border-gray-200 bg-gray-200 animate-pulse" />
                      </div>
                    </Flex>
                  </Flex>
                </div>

                {/* Right Column: Name + Location + About + Links */}
                <div className="w-full lg:col-span-2 flex flex-col order-2">
                  <Flex direction="column" className="gap-4 lg:gap-6">
                    {/* Header skeleton (name + share/edit + location) */}
                    <div className="pl-0 lg:pl-4 pr-2">
                      <Flex className="items-center justify-between gap-4 mb-2">
                        <div className="h-9 lg:h-12 w-48 lg:w-64 bg-gray-200 rounded animate-pulse" />
                        <Flex className="gap-4">
                          <div className="h-[34px] w-[34px] rounded-full bg-gray-200 animate-pulse" />
                          <div className="h-[34px] w-24 lg:w-28 rounded-full bg-gray-200 animate-pulse" />
                        </Flex>
                      </Flex>
                      <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
                    </div>

                    {/* About and Links skeleton */}
                    <div className="flex flex-col gap-4 lg:gap-6 pl-0 lg:pl-4 lg:pr-2">
                      <div className="flex flex-col gap-2">
                        <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
                        <div className="space-y-2">
                          <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                          <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                          <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
                        <div className="space-y-2">
                          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                          <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                          <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                        </div>
                      </div>
                    </div>
                  </Flex>
                </div>

                {/* Full width: Filters and Tracks Table */}
                <div className="w-full lg:col-span-3 flex flex-col order-3">
                  <Flex
                    direction={{ initial: "column", sm: "row" }}
                    className="items-start sm:items-center gap-4 w-full justify-between pt-6 pb-4 mb-10 pl-4 pr-2"
                  >
                    <Flex className="gap-2 flex-wrap">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="h-[34px] w-20 bg-gray-200 rounded-full animate-pulse"
                        />
                      ))}
                    </Flex>
                    <div className="h-[34px] w-[200px] bg-gray-200 rounded-full animate-pulse" />
                  </Flex>

                  <Box className="w-full pb-8">
                    <div className="space-y-0 divide-y divide-gray-200">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className="flex items-center gap-4 py-3 pl-4 pr-2 animate-pulse"
                        >
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0" />
                          <div className="flex-1 space-y-2">
                            <div className="h-4 w-48 bg-gray-200 rounded" />
                            <div className="h-3 w-32 bg-gray-200 rounded" />
                          </div>
                          <div className="h-4 w-12 bg-gray-200 rounded" />
                        </div>
                      ))}
                    </div>
                  </Box>
                </div>
              </div>
            </div>
          </Flex>
        </Flex>
      </ContainerWrapper>
    </div>
  );
}
